import NfseServicePicker from '../components/NfseServicePicker';
import { fiscalName } from '../utils/fiscalName';
import { splitCompanyAddress, joinCompanyAddress } from '../utils/companyAddress';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaBell, FaBuilding, FaCalendarAlt, FaClock, FaDownload, FaEdit, FaFileAlt, FaFolder, FaFolderOpen, FaHashtag, FaListAlt, FaMapMarkerAlt, FaPlus, FaSave, FaStickyNote, FaTrash, FaUsers } from 'react-icons/fa';
import api from '../api';
import NfseManager from './NfseManager';
import Customers from './Customers';
import CompanyNfseTaxSettings from './CompanyNfseTaxSettings';
import LinkBrandingSettings from '../components/LinkBrandingSettings';
import { getApiBase, isAbsoluteUrl, normalizeStoredUrl, resolveAssetUrl } from '../utils/assets';
import useAuthenticatedAssetUrl from '../hooks/useAuthenticatedAssetUrl';
import {
  getMaxUploadFileSizeMessage,
  getUploadLimitExceededMessage,
  isFileWithinUploadLimit,
  MAX_UPLOAD_FILE_SIZE_MB,
} from '../utils/uploadLimits';


const BUSINESS_DAY_LABELS = [
  { key: 'monday', label: 'Segunda-feira', short: 'Seg' },
  { key: 'tuesday', label: 'Terça-feira', short: 'Ter' },
  { key: 'wednesday', label: 'Quarta-feira', short: 'Qua' },
  { key: 'thursday', label: 'Quinta-feira', short: 'Qui' },
  { key: 'friday', label: 'Sexta-feira', short: 'Sex' },
  { key: 'saturday', label: 'Sábado', short: 'Sáb' },
  { key: 'sunday', label: 'Domingo', short: 'Dom' },
];

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

const DOCUMENT_CATEGORIES = [
  { key: 'certidoes', label: 'Certidões' },
  { key: 'contratos', label: 'Contratos' },
  { key: 'fiscais', label: 'Fiscais' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'operacionais', label: 'Operacionais' },
  { key: 'outros', label: 'Outros documentos' },
];

const DEFAULT_DOCUMENT_CATEGORY = DOCUMENT_CATEGORIES[0].key;

const mergeDocumentEdits = (baseline, edited, saved) => {
  const keyOf = doc => doc.id ?? doc.localId ?? doc.fileUrl;
  const original = new Map(baseline.map(doc => [keyOf(doc), doc]));
  const local = new Map(edited.map(doc => [keyOf(doc), doc]));
  const result = saved.flatMap(doc => {
    const key = keyOf(doc);
    if (original.has(key) && !local.has(key)) return [];
    if (original.has(key) && JSON.stringify(original.get(key)) !== JSON.stringify(local.get(key))) return [local.get(key)];
    return [doc];
  });
  edited.forEach(doc => { if (!original.has(keyOf(doc)) && !result.some(item => keyOf(item) === keyOf(doc))) result.push(doc); });
  return result;
};

const createDefaultBusinessHours = () => ({
  timezone: '',
  days: BUSINESS_DAY_LABELS.reduce((acc, { key }, index) => {
    const isWeekend = index >= 5;
    acc[key] = {
      enabled: !isWeekend,
      start: '09:00',
      end: isWeekend ? '13:00' : '18:00',
    };
    return acc;
  }, {}),
});

const cloneBusinessHoursState = (value) => {
  const fallback = createDefaultBusinessHours();
  if (!value || typeof value !== 'object') {
    return fallback;
  }

  const days = BUSINESS_DAY_LABELS.reduce((acc, { key }) => {
    const source = value?.days?.[key];
    if (source && typeof source === 'object') {
      const start = typeof source.start === 'string' && TIME_PATTERN.test(source.start)
        ? source.start
        : fallback.days[key].start;
      const end = typeof source.end === 'string' && TIME_PATTERN.test(source.end)
        ? source.end
        : fallback.days[key].end;
      acc[key] = {
        enabled: Boolean(source.enabled),
        start,
        end,
      };
    } else {
      acc[key] = { ...fallback.days[key] };
    }
    return acc;
  }, {});

  const timezone =
    typeof value.timezone === 'string'
      ? value.timezone
      : typeof value.timezone === 'number'
      ? String(value.timezone)
      : '';

  return {
    timezone,
    days,
  };
};

const prepareBusinessHoursPayload = (value) => {
  const normalized = cloneBusinessHoursState(value);
  const fallback = createDefaultBusinessHours();

  return {
    timezone: normalized.timezone?.trim() ? normalized.timezone.trim() : null,
    days: BUSINESS_DAY_LABELS.reduce((acc, { key }) => {
      const entry = normalized.days[key] || fallback.days[key];
      const start = TIME_PATTERN.test(entry.start) ? entry.start : fallback.days[key].start;
      const end = TIME_PATTERN.test(entry.end) ? entry.end : fallback.days[key].end;
      acc[key] = {
        enabled: Boolean(entry.enabled),
        start,
        end,
      };
      return acc;
    }, {}),
  };
};

const formatBusinessHoursSummary = (value) => {
  const normalized = cloneBusinessHoursState(value);
  const segments = [];
  let current = null;

  BUSINESS_DAY_LABELS.forEach(({ key, short }, index) => {
    const entry = normalized.days[key];
    const label = entry.enabled ? `${entry.start} - ${entry.end}` : 'fechado';
    if (!current) {
      current = { label, start: short, end: short };
    } else if (current.label === label) {
      current.end = short;
    } else {
      segments.push(
        current.start === current.end
          ? `${current.start}: ${current.label}`
          : `${current.start}-${current.end}: ${current.label}`
      );
      current = { label, start: short, end: short };
    }
    if (index === BUSINESS_DAY_LABELS.length - 1 && current) {
      segments.push(
        current.start === current.end
          ? `${current.start}: ${current.label}`
          : `${current.start}-${current.end}: ${current.label}`
      );
    }
  });

  const timezone = normalized.timezone?.trim();
  if (timezone) {
    segments.unshift(`Fuso: ${timezone}`);
  }

  return segments.length ? segments.join(' • ') : '—';
};

const emptyCompany = {
  name: '',
  tradeName: '',
  taxId: '',
  email: '',
  phone: '',
  website: '',
  logoUrl: '',
  addressLine1: '',
  addressNumber: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  notes: '',
  municipalRegistration: '',
  municipalityIbgeCode: '',
  taxRegime: '',
  specialTaxRegime: '',
  defaultServiceCode: '',
  defaultServiceDescription: '',
  employerInssExempt: false,
  employerInssPercentage: '20',
  pisExempt: false,
  documents: [],
  documentCategories: [],
  businessHours: createDefaultBusinessHours(),
};

const extractFilenameFromDisposition = (header) => {
  if (!header || typeof header !== 'string') {
    return '';
  }

  const utfMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].trim().replace(/"/g, ''));
    } catch (error) {
      return utfMatch[1].trim().replace(/"/g, '');
    }
  }

  const asciiMatch = header.match(/filename="?([^";]+)"?/i);
  if (asciiMatch?.[1]) {
    return asciiMatch[1].trim();
  }

  return '';
};

const getFilenameFromUrl = (value) => {
  if (!value) {
    return '';
  }

  try {
    const base = getApiBase();
    const parsed = isAbsoluteUrl(value)
      ? new URL(value)
      : base
      ? new URL(value, base.origin)
      : new URL(value, 'http://localhost');

    const nameParam = parsed.searchParams.get('name');
    if (nameParam) {
      return decodeURIComponent(nameParam);
    }

    const segments = parsed.pathname.split('/').filter(Boolean);
    const lastSegment = segments.pop();
    return lastSegment ? decodeURIComponent(lastSegment) : '';
  } catch (error) {
    return '';
  }
};

const normalizeValue = (value) => {
  if (Array.isArray(value)) {
    return JSON.stringify(
      value.map((item) => ({
        id: item?.id ?? null,
        title: item?.title || '',
        fileUrl: item?.fileUrl || '',
        originalName: item?.originalName || '',
        category: item?.category || DEFAULT_DOCUMENT_CATEGORY,
        expiresAt: item?.expiresAt || '',
      })),
    );
  }
  if (value && typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return '';
    }
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  return value || '';
};

const normalizeTaxId = (value) => (value ? value.replace(/\D/g, '') : '');
const onlyDigits = (value) => (value ? String(value).replace(/\D/g, '') : '');
const formatZipCode = (value) => { const digits = onlyDigits(value).slice(0, 8); return digits.length > 5 ? `${digits.slice(0,5)}-${digits.slice(5)}` : digits; };
const COMPANY_DOCUMENT_REMINDER_TAG = '[company-document-reminder]';
const createEmptyReminderForm = () => ({ id: null, documentKey: '', title: '', description: '', date: '', time: '', sendToAll: false, targetUserIds: [], targetSectorIds: [], sendEmail: false });
const FieldTitle = ({ icon: Icon, children }) => <span className="inline-flex items-center gap-2">{Icon ? <Icon className="text-blue-500" /> : null}{children}</span>;
const getReminderDocumentName = (value) => String(value || '').split(/\r?\n/).find((line) => line.startsWith('Documento:'))?.replace('Documento:', '').trim() || '';
const stripReminderTag = (value) => String(value || '').replace(COMPANY_DOCUMENT_REMINDER_TAG, '').split(/\r?\n/).filter((line) => !line.startsWith('Documento:')).join('\n').trim();
const toDateInputValue = (value) => { const date = new Date(value); return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10); };
const toTimeInputValue = (value, allDay) => { if (allDay) return ''; const date = new Date(value); if (Number.isNaN(date.getTime())) return ''; return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`; };

const formatTaxId = (value) => {
  const digits = normalizeTaxId(value).slice(0, 14);
  if (!digits) return '';
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

const cloneCompany = (data) => ({
  ...data,
  documents: Array.isArray(data.documents)
    ? data.documents.map((doc) => ({
        id: typeof doc.id === 'number' ? doc.id : undefined,
        title: doc.title || '',
        fileUrl: doc.fileUrl || '',
        localId: doc.localId,
        originalName: doc.originalName || '',
        category: doc.category || DEFAULT_DOCUMENT_CATEGORY,
        expiresAt: doc.expiresAt || '',
      }))
    : [],
  documentCategories: (data.documentCategories || []).map(item => ({ ...item })),
  businessHours: cloneBusinessHoursState(data.businessHours),
});

const createEmptyCompany = () => cloneCompany(emptyCompany);

const generateLocalId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const prepareCompanyState = (data = {}) => {
  const merged = createEmptyCompany();
  Object.keys(emptyCompany).forEach((key) => {
    if (key === 'documents') {
      merged.documents = Array.isArray(data.documents)
        ? data.documents
            .map((doc) => ({
              id: typeof doc.id === 'number' ? doc.id : undefined,
              title: doc.title || '',
              fileUrl: normalizeStoredUrl(
                doc.fileUrl || doc.downloadPath || doc.relativePath || ''
              ),
              originalName:
                doc.originalName || getFilenameFromUrl(doc.fileUrl || doc.downloadPath || ''),
              category: doc.category || DEFAULT_DOCUMENT_CATEGORY,
              expiresAt: doc.expiresAt || '',
                  }))
            .sort((a, b) => {
              if (typeof a.id === 'number' && typeof b.id === 'number') {
                return a.id - b.id;
              }
              if (typeof a.id === 'number') return -1;
              if (typeof b.id === 'number') return 1;
              return 0;
            })
        : [];
    } else if (key === 'documentCategories') {
      merged.documentCategories = Array.isArray(data.documentCategories) ? data.documentCategories : [];
    } else if (key === 'taxId') {
      merged.taxId = normalizeTaxId(data.taxId);
    } else if (key === 'logoUrl') {
      merged.logoUrl = normalizeStoredUrl(data.logoUrl);
    } else if (key === 'businessHours') {
      merged.businessHours = cloneBusinessHoursState(data.businessHours);
    } else if (['employerInssExempt', 'pisExempt'].includes(key)) {
      merged[key] = Boolean(data[key]);
    } else {
      merged[key] = normalizeValue(data[key]);
    }
  });
  const address = splitCompanyAddress(merged.addressLine1);
  merged.addressLine1 = address.street;
  merged.addressNumber = data.addressNumber !== undefined ? onlyDigits(data.addressNumber).slice(0, 30) : address.number;
  merged.originalAddressLine1 = data.addressLine1 || '';
  return cloneCompany(merged);
};

export default function CompanyProfile({ section = 'registration', openTab, initialPanel = 'company', refreshKey, initialFiscalContext }) {
  const [registrationPanel, setRegistrationPanel] = useState(initialPanel);
  useEffect(() => { setRegistrationPanel(initialPanel); }, [initialPanel, refreshKey]);
  const [company, setCompany] = useState(() => createEmptyCompany());
  const [initialCompany, setInitialCompany] = useState(() => createEmptyCompany());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [documentUploading, setDocumentUploading] = useState(false);
  const [downloadingDocumentKey, setDownloadingDocumentKey] = useState(null);
  const activeTab = section === 'documents' ? 'documents' : 'registration';
  const [zipCodeLoading, setZipCodeLoading] = useState(false);
  const [municipalityBusy, setMunicipalityBusy] = useState(false);
  const [municipalityMatches, setMunicipalityMatches] = useState([]);
  const [municipalityNotice, setMunicipalityNotice] = useState('');
  const postalLookupId = useRef(0);

  const [reminderForm, setReminderForm] = useState(() => createEmptyReminderForm());
  const [recipientOptions, setRecipientOptions] = useState(() => ({ users: [], sectors: [] }));
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [reminderSaving, setReminderSaving] = useState(false);
  const [documentReminders, setDocumentReminders] = useState([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [selectedDocumentCategory, setSelectedDocumentCategory] = useState(DEFAULT_DOCUMENT_CATEGORY);
  const [documentView, setDocumentView] = useState('folders');
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [documentNotice, setDocumentNotice] = useState('');
  const [documentError, setDocumentError] = useState('');

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    api
      .get('/company')
      .then((response) => {
        const data = response.data || {};
        if (!isMounted) return;
        const prepared = prepareCompanyState(data);
        setCompany(prepared);
        setInitialCompany(cloneCompany(prepared));
      })
      .catch(() => {
        if (!isMounted) return;
        const empty = prepareCompanyState();
        setCompany(empty);
        setInitialCompany(cloneCompany(empty));
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setRecipientsLoading(true);
    api.get('/calendar/recipients')
      .then((response) => {
        if (!isMounted) return;
        const users = Array.isArray(response.data?.users) ? response.data.users.map((item) => ({ id: Number(item?.id ?? item?.userId ?? item?.value), name: item?.name?.trim?.() || 'Usuário sem nome', sectorName: item?.sectorName || item?.sector?.name || '' })).filter((item) => Number.isInteger(item.id) && item.id > 0) : [];
        const sectors = Array.isArray(response.data?.sectors) ? response.data.sectors.map((item) => ({ id: Number(item?.id ?? item?.value), name: item?.name?.trim?.() || 'Setor' })).filter((item) => Number.isInteger(item.id) && item.id > 0) : [];
        setRecipientOptions({ users, sectors });
      })
      .catch(() => { if (isMounted) setRecipientOptions({ users: [], sectors: [] }); })
      .finally(() => { if (isMounted) setRecipientsLoading(false); });
    return () => { isMounted = false; };
  }, []);

  const loadDocumentReminders = async () => {
    setRemindersLoading(true);
    try {
      const response = await api.get('/calendar/events');
      const events = Array.isArray(response.data) ? response.data : [];
      setDocumentReminders(events.filter((event) => event.source === 'local' && String(event.description || '').includes(COMPANY_DOCUMENT_REMINDER_TAG)));
    } catch (error) {
      setDocumentReminders([]);
    } finally {
      setRemindersLoading(false);
    }
  };

  useEffect(() => {
    loadDocumentReminders();
  }, []);

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setCompany((prev) => {
      const next = cloneCompany(prev);
      next[field] = ['name', 'tradeName'].includes(field) ? fiscalName(value) : field === 'state' ? value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2) : value;
      // Nunca manter o IBGE da cidade anterior após trocar município ou UF.
      if ((field === 'city' || field === 'state') && next[field] !== prev[field]) {
        next.municipalityIbgeCode = '';
      }
      return next;
    });
  };

  useEffect(() => {
    if (loading || activeTab !== 'registration') return undefined;
    const city = String(company.city || '').trim();
    const uf = String(company.state || '').trim().toUpperCase();
    const code = onlyDigits(company.municipalityIbgeCode);
    if (!/^[A-Z]{2}$/.test(uf) || city.length < 2 || code.length === 7) {
      setMunicipalityMatches([]);
      setMunicipalityBusy(false);
      setMunicipalityNotice('');
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setMunicipalityBusy(true);
      setMunicipalityNotice('');
      try {
        const response = await api.get('/company/municipalities', { params: { uf, city } });
        if (cancelled) return;
        const { exact, matches } = response.data || {};
        if (exact && /^\d{7}$/.test(String(exact.id || ''))) {
          setCompany(prev => {
            if (String(prev.city || '').trim() !== city ||
                String(prev.state || '').trim().toUpperCase() !== uf ||
                onlyDigits(prev.municipalityIbgeCode)) return prev;
            return { ...cloneCompany(prev), municipalityIbgeCode: String(exact.id) };
          });
          setMunicipalityMatches([]);
          setMunicipalityNotice('Código IBGE confirmado automaticamente pelo catálogo oficial.');
        } else {
          const list = Array.isArray(matches) ? matches : [];
          setMunicipalityMatches(list);
          setMunicipalityNotice(list.length ? 'Escolha o município correto na lista para preencher o IBGE.'
            : 'Cidade não localizada na UF informada. Confira o nome ou preencha o código manualmente.');
        }
      } catch {
        if (!cancelled) {
          setMunicipalityMatches([]);
          setMunicipalityNotice('Consulta ao IBGE indisponível. Você ainda pode informar o código manualmente.');
        }
      } finally {
        if (!cancelled) setMunicipalityBusy(false);
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [company.city, company.state, company.municipalityIbgeCode, loading, activeTab]);

  const chooseMunicipality = (match) => {
    setCompany(prev => ({
      ...cloneCompany(prev), city: match.name,
      municipalityIbgeCode: String(match.id),
    }));
    setMunicipalityMatches([]);
    setMunicipalityNotice('Código IBGE selecionado do catálogo oficial.');
  };

  const handleZipCodeChange = async (event) => {
    const requestId = ++postalLookupId.current;
    const masked = formatZipCode(event.target.value);
    setCompany((prev) => ({ ...cloneCompany(prev), zipCode: masked }));
    const cep = onlyDigits(masked);
    if (cep.length !== 8) { setZipCodeLoading(false); return; }
    setZipCodeLoading(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (requestId === postalLookupId.current && !data?.erro) {
        setCompany((prev) => {
          if (onlyDigits(prev.zipCode) !== cep) return prev;
          const next = cloneCompany(prev);
          next.addressLine1 = data.logradouro || next.addressLine1;
          next.addressLine2 = [data.complemento, data.bairro].filter(Boolean).join(' - ') || next.addressLine2;
          next.city = data.localidade || next.city;
          next.state = data.uf || next.state;
          // ViaCEP também devolve o código oficial IBGE para a localidade.
          const officialCode = onlyDigits(data.ibge);
          next.municipalityIbgeCode = /^\d{7}$/.test(officialCode) ? officialCode : '';
          return next;
        });
        setMunicipalityMatches([]);
        setMunicipalityNotice('Localidade e código IBGE preenchidos a partir do CEP.');
      }
    } catch (error) {
      // CEP manual continua disponível se a busca externa falhar.
    } finally {
      if (requestId === postalLookupId.current) setZipCodeLoading(false);
    }
  };

  const handleTaxIdChange = (event) => {
    const digits = normalizeTaxId(event.target.value).slice(0, 14);
    setCompany((prev) => {
      const next = cloneCompany(prev);
      next.taxId = digits;
      return next;
    });
  };

  const handleBusinessHoursTimezoneChange = (event) => {
    const { value } = event.target;
    setCompany((prev) => {
      const next = cloneCompany(prev);
      next.businessHours.timezone = value;
      return next;
    });
  };

  const handleBusinessHoursToggle = (dayKey) => (event) => {
    const { checked } = event.target;
    setCompany((prev) => {
      const next = cloneCompany(prev);
      next.businessHours.days[dayKey] = {
        ...next.businessHours.days[dayKey],
        enabled: checked,
      };
      return next;
    });
  };

  const handleBusinessHoursTimeChange = (dayKey, field) => (event) => {
    const { value } = event.target;
    setCompany((prev) => {
      const next = cloneCompany(prev);
      next.businessHours.days[dayKey] = {
        ...next.businessHours.days[dayKey],
        [field]: value,
      };
      return next;
    });
  };

  const hasChanges = useMemo(() => {
    return Object.keys(emptyCompany).some((key) => {
      const current = normalizeValue(company[key]);
      const initial = normalizeValue(initialCompany[key]);
      return current !== initial;
    });
  }, [company, initialCompany]);

  const normalizedLogoUrl = company.logoUrl ? normalizeStoredUrl(company.logoUrl) : '';
  const inlineLogoUrl = /^(data:|blob:)/i.test(normalizedLogoUrl) ? normalizedLogoUrl : '';
  const externalLogoUrl = !inlineLogoUrl && normalizedLogoUrl && isAbsoluteUrl(normalizedLogoUrl) ? resolveAssetUrl(normalizedLogoUrl) : '';
  const internalLogoUrl = useAuthenticatedAssetUrl(normalizedLogoUrl, { path: normalizedLogoUrl && !inlineLogoUrl && !externalLogoUrl ? '/company/logo/content' : '' });
  const resolvedLogoUrl = inlineLogoUrl || externalLogoUrl || internalLogoUrl;
  const documentReminderOptions = useMemo(() => company.documents.map((doc, index) => ({ key: String(doc.id ?? doc.localId ?? index), title: doc.title || doc.originalName || `Documento ${index + 1}` })), [company.documents]);
  const documentCategories = useMemo(() => {
    const categories = new Map(DOCUMENT_CATEGORIES.map(item => [item.key, item]));
    company.documentCategories.forEach(item => { if (!categories.has(item.key)) categories.set(item.key, item); });
    company.documents.forEach(doc => { if (doc.category && !categories.has(doc.category)) categories.set(doc.category, { key: doc.category, label: doc.category }); });
    return [...categories.values()];
  }, [company.documentCategories, company.documents]);
  const getDocumentCategoryLabel = category => documentCategories.find(item => item.key === category)?.label || 'Documentos';
  const documentsByCategory = useMemo(() => documentCategories.reduce((acc, category) => {
    acc[category.key] = company.documents.filter((doc) => (doc.category || DEFAULT_DOCUMENT_CATEGORY) === category.key);
    return acc;
  }, {}), [company.documents, documentCategories]);
  const selectedCategoryDocuments = documentsByCategory[selectedDocumentCategory] || [];
  const selectedCategoryLabel = getDocumentCategoryLabel(selectedDocumentCategory);
  const expiringDocuments = useMemo(() => company.documents
    .map((doc, index) => ({ ...doc, documentIndex: index }))
    .filter((doc) => doc.expiresAt)
    .sort((a, b) => new Date(`${a.expiresAt}T00:00:00`).getTime() - new Date(`${b.expiresAt}T00:00:00`).getTime()), [company.documents]);

  const handleLogoUpload = async (event) => {
    const input = event.target;
    const file = input?.files?.[0];
    if (!file) return;

    if (!isFileWithinUploadLimit(file)) {
      alert(getMaxUploadFileSizeMessage());
      if (input) {
        input.value = '';
      }
      return;
    }

    const form = new FormData();
    form.append('file', file);
    setLogoUploading(true);
    try {
      const response = await api.post('/upload', form);
      const rawUrl =
        response.data?.downloadPath ||
        response.data?.downloadUrl ||
        response.data?.url ||
        '';
      const url = normalizeStoredUrl(rawUrl);
      if (!url) {
        throw new Error('missing url');
      }
      setCompany((prev) => {
        const next = cloneCompany(prev);
        next.logoUrl = url;
        return next;
      });
    } catch (error) {
      const message =
        error?.response?.status === 413
          ? getUploadLimitExceededMessage()
          : error?.response?.data?.error ||
            error?.message ||
            'Falha ao enviar a logo da empresa.';
      alert(message);
    } finally {
      setLogoUploading(false);
      if (input) {
        input.value = '';
      }
    }
  };

  const handleRemoveLogo = () => {
    setCompany((prev) => {
      if (!prev.logoUrl) return prev;
      const next = cloneCompany(prev);
      next.logoUrl = '';
      return next;
    });
  };

  const handleLogoError = () => {
    handleRemoveLogo();
  };

  const handleDocumentUpload = async (event) => {
    const input = event.target;
    const file = input?.files?.[0];
    if (!file) return;

    if (!isFileWithinUploadLimit(file)) {
      alert(getMaxUploadFileSizeMessage());
      if (input) {
        input.value = '';
      }
      return;
    }

    const form = new FormData();
    form.append('file', file);
    if (documentUploading || saving) return;
    setDocumentUploading(true);
    setDocumentNotice('');
    setDocumentError('');
    try {
      const response = await api.post('/upload', form);
      const rawUrl =
        response.data?.downloadPath || response.data?.downloadUrl || response.data?.url || '';
      const url = normalizeStoredUrl(rawUrl);
      if (!url) {
        throw new Error('missing url');
      }
      const baseTitle = file.name.replace(/\.[^.]+$/, '').slice(0, 200).trim();
      const title = baseTitle || 'Documento';
      const document = { title, fileUrl: url, originalName: file.name, category: selectedDocumentCategory, expiresAt: '' };
      const latest = prepareCompanyState((await api.get('/company')).data || {});
      const saved = await persistCompany({ ...latest, documents: [...latest.documents, document] });
      applyAutomaticSave(saved);
      setDocumentNotice('Arquivo adicionado e salvo automaticamente.');
    } catch (error) {
      const message =
        error?.response?.status === 413
          ? getUploadLimitExceededMessage()
          : error?.response?.data?.error ||
            error?.message ||
            'Falha ao enviar o documento.';
      setDocumentError('Não foi possível adicionar e salvar o documento. Tente selecionar o arquivo novamente. ' + message);
    } finally {
      setDocumentUploading(false);
      if (input) {
        input.value = '';
      }
    }
  };

  const handleDocumentFieldChange = (index, field) => (event) => {
    const { value } = event.target;
    setCompany((prev) => {
      const next = cloneCompany(prev);
      next.documents = next.documents.map((doc, docIndex) =>
        docIndex === index ? { ...doc, [field]: value } : doc
      );
      return next;
    });
  };

  const handleDocumentTitleChange = (index) => handleDocumentFieldChange(index, 'title');

  const handleRemoveDocument = (index) => {
    setCompany((prev) => {
      const next = cloneCompany(prev);
      next.documents = next.documents.filter((_, docIndex) => docIndex !== index);
      return next;
    });
  };

  const handleReminderChange = (field) => (event) => setReminderForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleReminderRecipientsChange = (field) => (event) => {
    const values = Array.from(event.target.selectedOptions || []).map((option) => Number(option.value)).filter((value) => Number.isInteger(value) && value > 0);
    setReminderForm((prev) => ({ ...prev, [field]: Array.from(new Set(values)) }));
  };

  const handleToggleDocumentReminder = (documentKey, expiresAt = '') => {
    setShowReminderForm((prev) => {
      const openingAnother = !prev || reminderForm.documentKey !== documentKey;
      if (openingAnother) {
        setReminderForm({ ...createEmptyReminderForm(), documentKey, date: expiresAt || '' });
        return true;
      }
      setReminderForm(createEmptyReminderForm());
      return false;
    });
  };

  const notifyReminderByEmail = async (eventId, form = reminderForm) => {
    if (!form.sendEmail || !eventId) return;
    await api.post(`/calendar/events/${eventId}/email`, {
      sendToAll: form.sendToAll,
      targetUserIds: form.targetUserIds,
      targetSectorIds: form.targetSectorIds,
    });
  };

  const handleEditReminder = (reminder) => {
    setShowReminderForm(true);
    setReminderForm({
      id: reminder.localId,
      documentKey: documentReminderOptions.find((doc) => doc.title === getReminderDocumentName(reminder.description))?.key || '',
      title: reminder.title || '',
      description: stripReminderTag(reminder.description),
      date: toDateInputValue(reminder.date),
      time: toTimeInputValue(reminder.date, reminder.allDay),
      sendToAll: false,
      targetUserIds: [],
      targetSectorIds: [],
      sendEmail: false,
    });
  };

  const handleDeleteReminder = async (reminder) => {
    if (!reminder?.localId) return;
    if (!window.confirm(`Excluir o lembrete "${reminder.title}"?`)) return;
    try {
      await api.delete(`/calendar/events/${reminder.localId}`);
      await loadDocumentReminders();
      if (reminderForm.id === reminder.localId) {
        setReminderForm(createEmptyReminderForm());
        setShowReminderForm(false);
      }
    } catch (error) {
      alert(error?.response?.data?.error || 'Não foi possível excluir o lembrete.');
    }
  };

  const handleCreateReminder = async (event) => {
    event.preventDefault();
    if (!reminderForm.date) { alert('Informe a data do lembrete.'); return; }
    const selectedDocument = documentReminderOptions.find((doc) => doc.key === reminderForm.documentKey);
    if (!selectedDocument) { alert('Selecione o documento vinculado ao lembrete.'); return; }
    const title = reminderForm.title.trim() || `Atualizar ${selectedDocument.title}`;
    const timeValue = reminderForm.time?.trim();
    const payload = {
      title,
      description: [COMPANY_DOCUMENT_REMINDER_TAG, `Documento: ${selectedDocument.title}`, reminderForm.description.trim()].filter(Boolean).join('\n'),
      date: timeValue ? new Date(`${reminderForm.date}T${timeValue}`).toISOString() : reminderForm.date,
      allDay: !timeValue,
      notifyOnDashboard: true,
    };
    if (!reminderForm.id) {
      if (reminderForm.sendToAll) payload.sendToAll = true;
      else {
        if (reminderForm.targetUserIds.length) payload.targetUserIds = reminderForm.targetUserIds;
        if (reminderForm.targetSectorIds.length) payload.targetSectorIds = reminderForm.targetSectorIds;
      }
    }
    setReminderSaving(true);
    try {
      const response = reminderForm.id
        ? await api.put(`/calendar/events/${reminderForm.id}`, payload)
        : await api.post('/calendar/events', payload);
      const eventId = reminderForm.id || response.data?.localId;
      await notifyReminderByEmail(eventId);
      setReminderForm(createEmptyReminderForm());
      setShowReminderForm(false);
      await loadDocumentReminders();
      alert(reminderForm.id ? 'Lembrete atualizado com sucesso.' : 'Lembrete criado com sucesso.');
    } catch (error) {
      alert(error?.response?.data?.error || 'Não foi possível salvar o lembrete.');
    } finally {
      setReminderSaving(false);
    }
  };

  const handleDownloadDocument = async (doc, index) => {
    const downloadKey = doc.id ?? doc.localId ?? index;
    if (!doc.id) {
      alert('Salve o documento antes de baixar o arquivo.');
      return;
    }
    const downloadPath = `/company/documents/${doc.id}/content`;

    setDownloadingDocumentKey(downloadKey);
    try {
      const response = await api.get(downloadPath, { responseType: 'blob' });
      const filenameFromHeader = extractFilenameFromDisposition(
        response.headers?.['content-disposition']
      );
      const fallbackName =
        filenameFromHeader || doc.originalName || doc.title || 'documento';

      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fallbackName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      alert('Não foi possível baixar o documento.');
    } finally {
      setDownloadingDocumentKey(null);
    }
  };

  const persistCompany = async (current) => {
    const { documents, taxId, originalAddressLine1, ...rest } = current;
    const payload = Object.fromEntries(
      Object.entries(rest).map(([key, value]) => [key, value?.trim ? value.trim() : value])
    );
    Object.keys(payload).forEach((key) => {
      if (typeof payload[key] === 'string' && payload[key].trim() === '') {
        payload[key] = null;
      }
    });
    payload.taxId = taxId ? normalizeTaxId(taxId) || null : null;
    // Persistir a linha completa mantém compatibilidade com as demais telas.
    payload.addressLine1 = joinCompanyAddress(current.addressLine1, current.addressNumber) || null;
    payload.addressNumber = onlyDigits(current.addressNumber).slice(0, 30) || null;
    if (activeTab === 'documents') {
      payload.addressLine1 = originalAddressLine1 || null;
      delete payload.addressNumber;
    }
    payload.logoUrl = normalizeStoredUrl(current.logoUrl) || null;
    payload.documents = documents
      .map((document) => ({
        id: typeof document.id === 'number' ? document.id : undefined,
        title: document.title?.trim() || '',
        fileUrl: normalizeStoredUrl(document.fileUrl?.trim() || ''),
        originalName: document.originalName || '',
        category: document.category || DEFAULT_DOCUMENT_CATEGORY,
        expiresAt: document.expiresAt || '',
      }))
      .filter((document) => document.title && document.fileUrl);

    payload.businessHours = prepareBusinessHoursPayload(current.businessHours);

    const response = await api.put('/company', payload);
    return prepareCompanyState(response.data || {});
  };

  const applyAutomaticSave = (saved) => {
    setCompany(prev => ({ ...saved, documents: mergeDocumentEdits(initialCompany.documents, prev.documents, saved.documents) }));
    setInitialCompany(cloneCompany(saved));
  };

  const handleCreateCategory = async (event) => {
    event.preventDefault();
    const label = newCategoryName.trim();
    if (!label || saving || documentUploading) return;
    if (documentCategories.some(item => item.label.toLocaleLowerCase('pt-BR') === label.toLocaleLowerCase('pt-BR'))) {
      setDocumentError('Já existe uma categoria com esse nome.');
      return;
    }
    setSaving(true);
    setDocumentError('');
    setDocumentNotice('');
    try {
      const category = { key: generateLocalId(), label };
      const latest = prepareCompanyState((await api.get('/company')).data || {});
      const saved = await persistCompany({ ...latest, documentCategories: [...latest.documentCategories, category] });
      applyAutomaticSave(saved);
      setSelectedDocumentCategory(category.key);
      setDocumentView('folders');
      setNewCategoryName('');
      setShowCategoryForm(false);
      setDocumentNotice('Categoria criada e salva automaticamente.');
    } catch (error) {
      setDocumentError(error.response?.data?.error || 'Não foi possível salvar a categoria. Tente novamente.');
    } finally { setSaving(false); }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (activeTab === 'registration' && !company.name.trim()) {
      alert('Informe o nome da empresa.');
      return;
    }
    if (!hasChanges || saving || documentUploading) return;

    setSaving(true);
    try {
      // Each menu entry owns one section. Read the other section before saving
      // so a previously opened page does not overwrite changes made there.
      const latest = prepareCompanyState((await api.get('/company')).data || {});
      const current = activeTab === 'documents'
        ? { ...latest, documents: mergeDocumentEdits(initialCompany.documents, company.documents, latest.documents) }
        : { ...company, documents: latest.documents, documentCategories: latest.documentCategories };
      const saved = await persistCompany(current);
      setCompany(saved);
      setInitialCompany(cloneCompany(saved));
      window.dispatchEvent(new Event('nfse-settings-updated'));
      alert(activeTab === 'documents' ? 'Documentação salva com sucesso.' : 'Dados da empresa salvos com sucesso.');
    } catch (error) {
      const message = error.response?.data?.error || 'Não foi possível salvar os dados.';
      alert(message);
    } finally {
      setSaving(false);
    }
  };


  const summaryItems = [
    { label: 'Nome fantasia', value: fiscalName(company.tradeName) || '—' },
    { label: 'CNPJ/CPF', value: formatTaxId(company.taxId) || '—' },
    { label: 'E-mail', value: company.email || '—' },
    { label: 'Telefone', value: company.phone || '—' },
    { label: 'Site', value: company.website || '—' },
    {
      label: 'Horário de funcionamento',
      value: formatBusinessHoursSummary(company.businessHours),
    },
    {
      label: 'Documentos',
      value: company.documents.length
        ? `${company.documents.length} arquivo${company.documents.length > 1 ? 's' : ''}`
        : '—',
    },
    {
      label: 'Endereço',
      value:
        company.addressLine1 || company.addressLine2 || company.city || company.state || company.zipCode
          ? [
              joinCompanyAddress(company.addressLine1, company.addressNumber),
              company.addressLine2,
              [company.city, company.state].filter(Boolean).join(' - '),
              company.zipCode,
            ]
              .filter((part) => part && part.trim())
              .join(' | ')
          : '—',
    },
  ];

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto">
        <p className="text-gray-600">Carregando informações...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {activeTab === 'registration' && <>
      <nav role="tablist" aria-label="Abas de Dados Cadastrais" className="flex flex-wrap gap-2 rounded-xl border bg-white p-2">
        {[['company', 'Empresa e endereço'], ['fiscal', 'Fiscal e alíquota da empresa'], ['certificate', 'Certificado e API'], ['templates', 'Modelos de notas'], ['automations', 'Automações'], ['customers', 'Clientes e descrições']].map(([id, label]) => <button key={id} type="button" role="tab" aria-selected={registrationPanel === id} tabIndex={registrationPanel === id ? 0 : -1} onKeyDown={event => {
          if (!['ArrowRight', 'ArrowLeft'].includes(event.key)) return;
          event.preventDefault();
          const tabs = Array.from(event.currentTarget.parentElement.querySelectorAll('[role="tab"]'));
          const next = (tabs.indexOf(event.currentTarget) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          tabs[next].focus(); tabs[next].click();
        }} onClick={() => setRegistrationPanel(id)} className={`min-h-[44px] rounded-lg px-3 text-sm font-semibold ${registrationPanel === id ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>{label}</button>)}
      </nav>
      {registrationPanel === 'company' && <>
      <section className="bg-white shadow rounded-lg p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border bg-gray-50">
              {resolvedLogoUrl ? (
                <img
                  src={resolvedLogoUrl}
                  alt="Logo da empresa"
                  className="h-full w-full object-contain"
                  onError={handleLogoError}
                />
              ) : (
                <span className="text-sm text-gray-400">Sem logo</span>
              )}
            </div>
            <div>
              <p className="text-lg font-medium">Identidade visual</p>
              <p className="text-sm text-gray-500">
                Utilize arquivos PNG ou SVG com fundo transparente para melhor resultado. Tamanho máximo: {MAX_UPLOAD_FILE_SIZE_MB}
                MB.
              </p>
            </div>
          </div>
          <div className="flex flex-col items-start gap-2 md:items-end">
            <label className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 shadow-sm transition hover:bg-blue-100">
              <input type="file" className="hidden" onChange={handleLogoUpload} disabled={logoUploading} />
              <span>{logoUploading ? 'Enviando...' : 'Enviar logo'}</span>
            </label>
            {company.logoUrl && (
              <button
                type="button"
                onClick={handleRemoveLogo}
                className="text-xs text-red-500 hover:underline"
              >
                Remover logo
              </button>
            )}
          </div>
        </div>
      </section>

      <LinkBrandingSettings />

      <section className="grid gap-6 lg:grid-cols-3">
        <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-5 bg-white shadow rounded-lg p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              <FieldTitle icon={FaBuilding}>Razão social *</FieldTitle>
              <input
                type="text"
                value={fiscalName(company.name)}
                onChange={handleChange('name')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Nome legal da empresa"
                required
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              <FieldTitle icon={FaBuilding}>Nome fantasia</FieldTitle>
              <input
                type="text"
                value={fiscalName(company.tradeName)}
                onChange={handleChange('tradeName')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Como sua empresa é conhecida"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              <FieldTitle icon={FaHashtag}>CNPJ/CPF</FieldTitle>
              <input
                type="text"
                value={formatTaxId(company.taxId)}
                onChange={handleTaxIdChange}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="00.000.000/0000-00"
                inputMode="numeric"
                maxLength={18}
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              <FieldTitle icon={FaFileAlt}>Site</FieldTitle>
              <input
                type="text"
                value={company.website}
                onChange={handleChange('website')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="suaempresa.com"
                inputMode="url"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              <FieldTitle icon={FaFileAlt}>E-mail de contato</FieldTitle>
              <input
                type="email"
                value={company.email}
                onChange={handleChange('email')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="contato@suaempresa.com"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              <FieldTitle icon={FaFileAlt}>Telefone</FieldTitle>
              <input
                type="tel"
                value={company.phone}
                onChange={handleChange('phone')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="(00) 0000-0000"
              />
            </label>
          </div>

          <div className="space-y-4 rounded-lg border border-gray-200 p-4">
            <p className="text-sm font-semibold text-gray-700">Configurações de encargos trabalhistas</p>
            <div className="grid gap-4 sm:grid-cols-3">
              <label className="text-sm font-medium text-gray-700">
                Isenção de INSS patronal
                <select
                  value={company.employerInssExempt ? 'true' : 'false'}
                  onChange={(event) => setCompany((prev) => ({ ...cloneCompany(prev), employerInssExempt: event.target.value === 'true' }))}
                  className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="false">Não</option>
                  <option value="true">Sim</option>
                </select>
              </label>
              <label className="text-sm font-medium text-gray-700">
                Porcentagem de INSS patronal (%)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={company.employerInssPercentage}
                  onChange={handleChange('employerInssPercentage')}
                  disabled={company.employerInssExempt}
                  className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </label>
              <label className="text-sm font-medium text-gray-700">
                Isenção de PIS
                <select
                  value={company.pisExempt ? 'true' : 'false'}
                  onChange={(event) => setCompany((prev) => ({ ...cloneCompany(prev), pisExempt: event.target.value === 'true' }))}
                  className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="false">Não</option>
                  <option value="true">Sim</option>
                </select>
              </label>
            </div>
          </div>

          <div className="space-y-4 rounded-lg border border-gray-200 p-4">
            <div>
              <p className="text-sm font-medium text-gray-700"><FieldTitle icon={FaClock}>Horário de funcionamento</FieldTitle></p>
              <p className="text-xs text-gray-500">
                Defina os horários utilizados no cálculo de SLA. Dias sem expediente não contarão como atraso.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-medium text-gray-700">
                <FieldTitle icon={FaClock}>Fuso horário (opcional)</FieldTitle>
                <input
                  type="text"
                  value={company.businessHours.timezone}
                  onChange={handleBusinessHoursTimezoneChange}
                  className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Ex: America/Sao_Paulo"
                />
              </label>
            </div>
            <div className="space-y-3">
              {BUSINESS_DAY_LABELS.map(({ key, label }) => {
                const schedule = company.businessHours.days[key];
                return (
                  <div
                    key={key}
                    className="flex flex-col gap-3 rounded-md border border-gray-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={schedule.enabled}
                        onChange={handleBusinessHoursToggle(key)}
                      />
                      {label}
                    </label>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                      <span className="hidden sm:inline">Das</span>
                      <input
                        type="time"
                        value={schedule.start}
                        onChange={handleBusinessHoursTimeChange(key, 'start')}
                        disabled={!schedule.enabled}
                        className="rounded border px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed"
                      />
                      <span>às</span>
                      <input
                        type="time"
                        value={schedule.end}
                        onChange={handleBusinessHoursTimeChange(key, 'end')}
                        disabled={!schedule.enabled}
                        className="rounded border px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              <FieldTitle icon={FaHashtag}>CEP</FieldTitle>
              <input
                type="text"
                value={company.zipCode}
                onChange={handleZipCodeChange}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder={zipCodeLoading ? 'Buscando CEP...' : '00000-000'}
              />
            </label>
            <label className="text-sm font-medium text-gray-700 sm:col-span-2">
              <FieldTitle icon={FaMapMarkerAlt}>Endereço</FieldTitle>
              <input
                type="text"
                value={company.addressLine1}
                onChange={handleChange('addressLine1')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Rua ou avenida"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              <FieldTitle icon={FaHashtag}>Número</FieldTitle>
              <input type="text" inputMode="numeric" maxLength={30} value={company.addressNumber}
                onChange={event => setCompany(prev => ({ ...cloneCompany(prev), addressNumber: onlyDigits(event.target.value).slice(0, 30) }))}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Somente números" />
            </label>
            <label className="text-sm font-medium text-gray-700 sm:col-span-2">
              <FieldTitle icon={FaStickyNote}>Complemento</FieldTitle>
              <input
                type="text"
                value={company.addressLine2}
                onChange={handleChange('addressLine2')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Sala, andar, bairro"
              />
            </label>
            <div className="text-sm font-medium text-gray-700">
              <label htmlFor="company-city"><FieldTitle icon={FaMapMarkerAlt}>Cidade</FieldTitle></label>
              <input id="company-city"
                type="text"
                value={company.city}
                onChange={handleChange('city')}
                autoComplete="address-level2"
                className="mt-1 w-full rounded border px-3 py-2 text-base sm:text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {municipalityMatches.length > 0 && <div role="region" aria-label="Municípios encontrados" className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-blue-100 bg-white shadow-sm">
                {municipalityMatches.map(match => <button key={match.id} type="button"
                  onClick={() => chooseMunicipality(match)}
                  className="flex min-h-[44px] w-full items-center justify-between gap-2 border-b px-3 py-2 text-left text-xs last:border-b-0 hover:bg-blue-50 focus-visible:bg-blue-50">
                  <span>{match.name}</span><span className="font-mono text-slate-500">{match.id}</span>
                </button>)}
              </div>}
            </div>
            <label className="text-sm font-medium text-gray-700">
              <FieldTitle icon={FaMapMarkerAlt}>Estado</FieldTitle>
              <input
                type="text"
                value={company.state}
                onChange={handleChange('state')}
                maxLength={2} placeholder="UF (ex.: SP)" autoComplete="address-level1"
                className="mt-1 w-full rounded border px-3 py-2 text-base sm:text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </label>
          </div>

          <label className="block text-sm font-medium text-gray-700">
            <FieldTitle icon={FaStickyNote}>Observações</FieldTitle>
            <textarea
              value={company.notes}
              onChange={handleChange('notes')}
              className="mt-1 h-28 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Informações adicionais sobre a empresa"
            />
          </label>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setCompany(cloneCompany(initialCompany))}
              disabled={!hasChanges || saving}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Descartar alterações
            </button>
            <button
              type="submit"
              disabled={!hasChanges || saving}
              className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
            >
              {saving ? 'Salvando...' : 'Salvar dados'}
            </button>
          </div>
        </form>

        <aside className="space-y-4 rounded-lg bg-white p-5 shadow">
          <h2 className="text-lg font-semibold text-gray-800">Resumo rápido</h2>
          <dl className="space-y-3">
            {summaryItems.map((item) => (
              <div key={item.label} className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {item.label}
                </dt>
                <dd className="text-sm text-gray-700 break-words">{item.value}</dd>
              </div>
            ))}
          </dl>
          {company.notes && (
            <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-700">
              <p className="font-medium">Observações</p>
              <p>{company.notes}</p>
            </div>
          )}
        </aside>
      </section>
      </>}
      {registrationPanel === 'fiscal' && <div className="space-y-4">
        <form onSubmit={handleSubmit} className="rounded-xl border bg-white p-4 shadow-sm" aria-label="Cadastro fiscal da empresa">
          <div className="space-y-4 rounded-xl border border-blue-100 bg-blue-50/40 p-4">
            <div>
              <p className="text-sm font-semibold text-gray-800">Informações fiscais · NFS-e</p>
              <p className="mt-1 text-xs text-gray-600">Dados da empresa emissora. O Gerenciador NFS utilizará essas informações automaticamente; confira os parâmetros com a contabilidade antes de emitir.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-medium text-gray-700">Inscrição municipal
                <input value={company.municipalRegistration} onChange={handleChange('municipalRegistration')} maxLength={30} className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" placeholder="Inscrição municipal" />
              </label>
              <div className="text-sm font-medium text-gray-700">
                <label htmlFor="company-municipality-ibge">Código IBGE do município (7 dígitos)</label>
                <input id="company-municipality-ibge" value={company.municipalityIbgeCode}
                  onChange={(event) => setCompany(prev => ({ ...cloneCompany(prev), municipalityIbgeCode: event.target.value.replace(/\D/g, '').slice(0, 7) }))}
                  inputMode="numeric" maxLength={7} autoComplete="off"
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-base sm:text-sm" placeholder={municipalityBusy ? 'Consultando IBGE...' : 'Preenchimento automático'} />
                <p className="mt-1 text-xs font-normal text-slate-500">
                  {municipalityBusy ? 'Consultando o IBGE...' : onlyDigits(company.municipalityIbgeCode).length === 7
                    ? 'Código de 7 dígitos informado. Confira a cidade e a UF antes de salvar.'
                    : municipalityNotice || 'Preenchido pelo CEP ou ao informar cidade e UF.'}
                </p>
              </div>
              <label className="text-sm font-medium text-gray-700">Regime tributário
                <select value={company.taxRegime} onChange={handleChange('taxRegime')} className="mt-1 w-full rounded-lg border px-3 py-2 text-sm">
                  <option value="">Selecione</option>
                  <option value="simples_nacional">Simples Nacional</option>
                  <option value="mei">MEI / SIMEI</option>
                  <option value="lucro_presumido">Lucro presumido</option>
                  <option value="lucro_real">Lucro real</option>
                  <option value="outro">Outro</option>
                </select>
              </label>
              <label className="text-sm font-medium text-gray-700">Regime especial (código oficial, se aplicável)
                <input value={company.specialTaxRegime} onChange={handleChange('specialTaxRegime')} maxLength={20} className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" placeholder="Conforme orientação contábil" />
              </label>
              <NfseServicePicker label="Código de serviço padrão da empresa" value={company.defaultServiceCode} onChange={code => setCompany(prev => ({ ...cloneCompany(prev), defaultServiceCode: code }))} />
              <label className="text-sm font-medium text-gray-700 sm:col-span-2">Descrição geral das notas
                <textarea value={company.defaultServiceDescription} onChange={handleChange('defaultServiceDescription')} maxLength={4000} rows={3} className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" placeholder="Descrição utilizada no preenchimento de novas notas" />
              </label>
            </div>
          </div>

          <div className="flex justify-end"><button type="submit" disabled={!hasChanges || saving} className="min-h-[44px] rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white disabled:opacity-50">{saving ? 'Salvando...' : 'Salvar cadastro fiscal'}</button></div>
        </form>
        <CompanyNfseTaxSettings />
      </div>}
      {['certificate', 'templates', 'automations'].includes(registrationPanel) && <NfseManager key={registrationPanel} configurationOnly initialView={registrationPanel === 'certificate' ? 'settings' : registrationPanel} openTab={openTab} />}
      {registrationPanel === 'customers' && <Customers openTab={openTab} />}
      </>}

      {activeTab === 'documents' && (
      <section className="bg-white shadow rounded-lg p-5">
        <header className="-mx-5 -mt-5 flex flex-col gap-3 rounded-t-lg bg-[#071f54] px-5 py-5 text-white sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="inline-flex items-center gap-2 text-lg font-semibold text-white"><FaFileAlt className="text-white" /> Documentação</h2>
          </div>
        </header>
        <fieldset disabled={saving || documentUploading} className="min-w-0">
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <label
            className={`inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 shadow-sm transition ${
              documentUploading ? 'cursor-not-allowed opacity-60 pointer-events-none' : 'hover:bg-blue-100'
            }`}
            aria-disabled={documentUploading}
          >
            <input
              type="file"
              className="hidden"
              aria-label="Adicionar documento"
              onChange={handleDocumentUpload}
              disabled={documentUploading}
            />
            <FaPlus /><span>{documentUploading ? 'Enviando...' : `Adicionar em ${selectedCategoryLabel}`}</span>
          </label>
          {hasChanges && <button type="button" onClick={() => handleSubmit({ preventDefault: () => {} })} disabled={!hasChanges || saving} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"><FaSave /> {saving ? 'Salvando...' : 'Salvar documentos'}</button>}
          <p className="text-xs text-slate-500">
            Tamanho máximo do arquivo: {MAX_UPLOAD_FILE_SIZE_MB} MB.
          </p>
        </div>
        {documentNotice && <p role="status" className="mt-3 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">{documentNotice}</p>}
        {documentError && <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{documentError}</p>}

        <div className="mt-5 flex flex-wrap gap-2 border-b border-slate-200">
          {[
            { key: 'folders', label: 'Pastas', icon: FaFolderOpen },
            { key: 'validity', label: 'Validades', icon: FaListAlt },
          ].map((view) => {
            const Icon = view.icon;
            const active = documentView === view.key;
            return (
              <button
                key={view.key}
                type="button"
                onClick={() => setDocumentView(view.key)}
                className={`inline-flex items-center gap-2 rounded-t-lg border px-4 py-2 text-sm font-semibold transition ${
                  active
                    ? 'border-blue-200 border-b-white bg-white text-blue-700'
                    : 'border-transparent text-slate-500 hover:bg-slate-50'
                }`}
              >
                <Icon /> {view.label}
              </button>
            );
          })}
        </div>

        {documentView === 'folders' && (
        <div className="mt-4 grid gap-4 lg:grid-cols-[280px_1fr]">
          <aside className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="mb-3 flex items-center justify-between px-1">
              <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700"><FaFolderOpen className="text-blue-500" /> Categorias</h3>
              <span className="text-xs font-medium text-slate-500">{company.documents.length} arquivos</span>
            </div>
            <button type="button" onClick={() => { setShowCategoryForm(!showCategoryForm); setDocumentError(''); }} className="mb-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50"><FaPlus /> Nova categoria</button>
            {showCategoryForm && <form onSubmit={handleCreateCategory} className="mb-3 space-y-2 rounded-lg border border-blue-200 bg-white p-3">
              <label className="block text-xs font-semibold text-slate-700">Nome da categoria<input autoFocus required maxLength={80} value={newCategoryName} onChange={event => setNewCategoryName(event.target.value)} className="mt-1 w-full rounded border border-slate-300 px-2 py-2 text-sm" /></label>
              <div className="flex flex-wrap gap-2"><button type="submit" disabled={!newCategoryName.trim()} className="rounded bg-blue-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">Criar categoria</button><button type="button" onClick={() => setShowCategoryForm(false)} className="px-2 py-2 text-xs text-slate-600">Cancelar</button></div>
            </form>}
            <div className="space-y-2">
              {documentCategories.map((category) => {
                const total = documentsByCategory[category.key]?.length || 0;
                const selected = selectedDocumentCategory === category.key;
                return (
                  <button
                    key={category.key}
                    type="button"
                    onClick={() => setSelectedDocumentCategory(category.key)}
                    className={`flex w-full items-center justify-between rounded-lg border px-3 py-3 text-left text-sm transition ${
                      selected
                        ? 'border-blue-300 bg-blue-100 text-blue-800 shadow-sm'
                        : 'border-transparent bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50'
                    }`}
                  >
                    <span className="inline-flex items-center gap-2 font-semibold"><FaFolder className={selected ? 'text-blue-600' : 'text-amber-500'} /> {category.label}</span>
                    <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold text-slate-500">{total}</span>
                  </button>
                );
              })}
            </div>
          </aside>

          <div className="rounded-xl border border-slate-200 bg-white">
            <div className="flex flex-col gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="inline-flex items-center gap-2 font-semibold text-slate-800"><FaFolderOpen className="text-blue-500" /> {selectedCategoryLabel}</h3>
                <p className="text-xs text-slate-500">Visualize, altere e exclua os documentos desta pasta.</p>
              </div>
              <span className="text-xs font-medium text-slate-500">{selectedCategoryDocuments.length} documento(s)</span>
            </div>

            {company.documents.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">
                Nenhum documento cadastrado até o momento. Utilize o botão acima para enviar arquivos.
              </p>
            ) : selectedCategoryDocuments.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">Esta categoria ainda não possui documentos.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {selectedCategoryDocuments.map((doc) => {
                  const index = company.documents.indexOf(doc);
                  const docKey = doc.id ?? doc.localId ?? index;
                  const isDownloading = downloadingDocumentKey === docKey;
                  return (
                    <li key={docKey} className="p-4 transition hover:bg-slate-50">
                      <div className="grid gap-4 xl:grid-cols-[1fr_170px]">
                        <div className="space-y-3">
                          <div className="flex items-start gap-3">
                            <div className="rounded-lg bg-blue-50 p-3 text-blue-600"><FaFileAlt /></div>
                            <div className="grid flex-1 gap-3 md:grid-cols-2">
                              <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 md:col-span-2">
                                Nome do documento
                                <input
                                  type="text"
                                  value={doc.title}
                                  onChange={handleDocumentTitleChange(index)}
                                  className="mt-1 w-full rounded border px-3 py-2 text-sm font-normal normal-case tracking-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  placeholder="Nome do arquivo"
                                  maxLength={200}
                                />
                              </label>
                              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                Categoria
                                <div className="mt-1 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold normal-case tracking-normal text-slate-700">
                                  {getDocumentCategoryLabel(doc.category || DEFAULT_DOCUMENT_CATEGORY)}
                                </div>
                              </div>
                              <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                Validade
                                <input type="date" value={doc.expiresAt || ''} onChange={handleDocumentFieldChange(index, 'expiresAt')} className="mt-1 w-full rounded border px-3 py-2 text-sm font-normal normal-case tracking-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                              </label>
                            </div>
                          </div>
                          <label className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700">
                            <input type="checkbox" checked={showReminderForm && reminderForm.documentKey === String(docKey)} onChange={() => handleToggleDocumentReminder(String(docKey), doc.expiresAt || '')} />
                            Adicionar lembrete {doc.expiresAt ? `para validade (${new Date(`${doc.expiresAt}T00:00:00`).toLocaleDateString('pt-BR')})` : ''}
                          </label>
                        </div>
                        <div className="flex flex-wrap items-start gap-2 xl:flex-col">
                          <button type="button" onClick={() => handleDownloadDocument(doc, index)} disabled={isDownloading} className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 shadow-sm transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"><FaDownload /> {isDownloading ? 'Baixando...' : 'Baixar'}</button>
                          <button type="button" onClick={() => handleRemoveDocument(index)} className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50"><FaTrash /> Excluir</button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
        )}

        {documentView === 'validity' && (
          <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
              <h3 className="inline-flex items-center gap-2 font-semibold text-slate-800"><FaCalendarAlt className="text-blue-500" /> Documentos com validade</h3>
              <p className="text-xs text-slate-500">Lista ordenada para mostrar primeiro os documentos mais próximos de vencer.</p>
            </div>
            {expiringDocuments.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">Nenhum documento com validade informada.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3 text-left">Documento</th>
                      <th className="px-4 py-3 text-left">Categoria</th>
                      <th className="px-4 py-3 text-left">Validade</th>
                      <th className="px-4 py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {expiringDocuments.map((doc) => {
                      const docKey = doc.id ?? doc.localId ?? doc.documentIndex;
                      const isDownloading = downloadingDocumentKey === docKey;
                      return (
                        <tr key={docKey} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-800">{doc.title || doc.originalName || 'Documento'}</td>
                          <td className="px-4 py-3 text-slate-600">{getDocumentCategoryLabel(doc.category || DEFAULT_DOCUMENT_CATEGORY)}</td>
                          <td className="px-4 py-3 font-semibold text-slate-700">{new Date(`${doc.expiresAt}T00:00:00`).toLocaleDateString('pt-BR')}</td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-2">
                              <button type="button" onClick={() => handleToggleDocumentReminder(String(docKey), doc.expiresAt || '')} className="inline-flex items-center gap-1 rounded-md border border-blue-200 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50"><FaBell /> Lembrete</button>
                              <button type="button" onClick={() => handleDownloadDocument(doc, doc.documentIndex)} disabled={isDownloading} className="inline-flex items-center gap-1 rounded-md border border-blue-200 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-60"><FaDownload /> {isDownloading ? 'Baixando...' : 'Baixar'}</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}


        {company.documents.length > 0 ? (
        <>
        {showReminderForm && (
        <form onSubmit={handleCreateReminder} className="mt-3 rounded-lg border border-blue-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-blue-700"><FaBell /> Lembrete opcional</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm font-medium text-gray-700 md:col-span-2"><FieldTitle icon={FaFileAlt}>Documento vinculado</FieldTitle><select className="mt-1 w-full rounded border px-3 py-2 text-sm" value={reminderForm.documentKey} onChange={handleReminderChange('documentKey')} required><option value="">Selecione um documento</option>{documentReminderOptions.map((doc) => <option key={doc.key} value={doc.key}>{doc.title}</option>)}</select></label>
            <label className="text-sm font-medium text-gray-700"><FieldTitle icon={FaBell}>Título do lembrete</FieldTitle><input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={reminderForm.title} onChange={handleReminderChange('title')} placeholder="Ex: Atualização de documento" /></label>
            <label className="text-sm font-medium text-gray-700"><FieldTitle icon={FaCalendarAlt}>Data</FieldTitle><input type="date" className="mt-1 w-full rounded border px-3 py-2 text-sm" value={reminderForm.date} onChange={handleReminderChange('date')} required /></label>
            <label className="text-sm font-medium text-gray-700"><FieldTitle icon={FaClock}>Horário</FieldTitle><input type="time" className="mt-1 w-full rounded border px-3 py-2 text-sm" value={reminderForm.time} onChange={handleReminderChange('time')} /></label>
            <label className="text-sm font-medium text-gray-700"><FieldTitle icon={FaUsers}>Usuários</FieldTitle><select multiple className="mt-1 min-h-24 w-full rounded border px-3 py-2 text-sm" value={reminderForm.targetUserIds.map(String)} onChange={handleReminderRecipientsChange('targetUserIds')} disabled={reminderForm.sendToAll || recipientsLoading}>{recipientOptions.users.map((item) => <option key={item.id} value={item.id}>{item.name}{item.sectorName ? ` · ${item.sectorName}` : ''}</option>)}</select></label>
            <label className="text-sm font-medium text-gray-700"><FieldTitle icon={FaUsers}>Setores</FieldTitle><select multiple className="mt-1 min-h-24 w-full rounded border px-3 py-2 text-sm" value={reminderForm.targetSectorIds.map(String)} onChange={handleReminderRecipientsChange('targetSectorIds')} disabled={reminderForm.sendToAll || recipientsLoading}>{recipientOptions.sectors.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
            <label className="text-sm font-medium text-gray-700 md:col-span-2"><FieldTitle icon={FaStickyNote}>Descrição</FieldTitle><textarea className="mt-1 h-20 w-full rounded border px-3 py-2 text-sm" value={reminderForm.description} onChange={handleReminderChange('description')} placeholder="Detalhes sobre o documento que precisa ser atualizado." /></label>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={reminderForm.sendToAll} onChange={(e) => setReminderForm((prev) => ({ ...prev, sendToAll: e.target.checked, targetUserIds: [], targetSectorIds: [] }))} disabled={Boolean(reminderForm.id)} /> Enviar para todos os usuários</label>
          <label className="mt-2 flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={reminderForm.sendEmail} onChange={(e) => setReminderForm((prev) => ({ ...prev, sendEmail: e.target.checked }))} /> Enviar também por e-mail aos destinatários selecionados</label>
          {reminderForm.id && <p className="mt-2 text-xs text-amber-700">Ao alterar, selecione novamente usuários ou setores se quiser reenviar por e-mail.</p>}
          <div className="mt-3 flex justify-end gap-2">{reminderForm.id && <button type="button" onClick={() => { setReminderForm(createEmptyReminderForm()); setShowReminderForm(false); }} className="rounded-lg border border-blue-300 px-4 py-2 text-sm font-semibold text-blue-700">Cancelar edição</button>}<button type="submit" disabled={reminderSaving} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-blue-700 disabled:opacity-60"><FaSave /> {reminderSaving ? 'Salvando...' : reminderForm.id ? 'Atualizar lembrete' : 'Criar lembrete'}</button></div>
        </form>
        )}

        {documentReminders.length > 0 && (
        <div className="mt-4 rounded-lg border border-gray-200 p-4">
          <h3 className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-gray-800"><FaBell className="text-blue-500" /> Lembretes</h3>
          {remindersLoading ? (
            <p className="text-sm text-gray-500">Carregando...</p>
          ) : (
            <ul className="space-y-2">
              {documentReminders.map((reminder) => (
                <li key={reminder.localId} className="flex flex-col gap-3 rounded-md border border-gray-100 p-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="font-medium text-gray-800">{reminder.title}</p>
                    <p className="text-xs text-gray-500">{new Date(reminder.date).toLocaleString('pt-BR')}</p>
                    {getReminderDocumentName(reminder.description) && <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Documento: {getReminderDocumentName(reminder.description)}</p>}
                    {stripReminderTag(reminder.description) && <p className="text-sm text-gray-600">{stripReminderTag(reminder.description)}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => handleEditReminder(reminder)} className="inline-flex items-center gap-1 rounded-md border border-blue-200 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"><FaEdit /> Alterar</button>
                    <button type="button" onClick={() => handleDeleteReminder(reminder)} className="inline-flex items-center gap-1 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"><FaTrash /> Excluir</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        )}
        </>
        ) : null}
        </fieldset>
      </section>
      )}
    </div>
  );
}

